import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Heart, Calendar, Users, Gift, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { SEO } from '../components/common/SEO';
import { SignInModal } from '../components/SignInModal';
import { SignUpModal } from '../components/SignUpModal';
import { useAuth } from '../hooks/useAuth';
import { useWishlists } from '../hooks/useWishlists';
import { useGuestWishlists } from '../hooks/useGuestWishlists';
import { useCanonicalUrl } from '../hooks/useCanonicalUrl';
import toast from 'react-hot-toast';

export const Dashboard: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const { wishlists, loading: wishlistsLoading, createWishlist } = useWishlists();
  const { wishlists: guestWishlists, createWishlist: createGuestWishlist } = useGuestWishlists();
  const [newWishlistTitle, setNewWishlistTitle] = useState('');
  const [newWishlistDescription, setNewWishlistDescription] = useState('');
  const [isCreatingWishlist, setIsCreatingWishlist] = useState(false);
  const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);
  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);
  const canonicalUrl = useCanonicalUrl();

  const handleCreateWishlist = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newWishlistTitle.trim()) return;

    setIsCreatingWishlist(true);
    try {
      if (user) {
        // Authenticated user - save to database
        await createWishlist(newWishlistTitle, newWishlistDescription);
      } else {
        // Guest user - save to localStorage
        createGuestWishlist(newWishlistTitle, newWishlistDescription);
      }
      
      setNewWishlistTitle('');
      setNewWishlistDescription('');
      toast.success('Wishlist created!');
    } catch (error: any) {
      toast.error(error.message || 'Failed to create wishlist');
    } finally {
      setIsCreatingWishlist(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Get current wishlists (authenticated or guest)
  const currentWishlists = user ? wishlists : guestWishlists;
  const currentWishlistsLoading = user ? wishlistsLoading : false;

  return (
    <>
      <SEO
        title="Dashboard - My Wishlists"
        description="Manage your wishlists, create new ones, and organize your gift planning."
        url={canonicalUrl}
      />

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {user ? `Welcome back, ${user.email}!` : 'Welcome to Wishful!'}
            </h1>
            <p className="text-gray-600">
              {user 
                ? 'Manage your wishlists and plan your perfect gifts.'
                : 'Create wishlists and plan your perfect gifts. Sign in to save and share your lists.'
              }
            </p>
            
            {/* Guest user sign-in prompt */}
            {!user && (
              <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-gray-800">Want to save and share your wishlists?</h3>
                    <p className="text-sm text-gray-600">Sign in to sync across devices and share with friends.</p>
                  </div>
                  <div className="flex space-x-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setIsSignInModalOpen(true)}
                    >
                      Sign In
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Link to="/holiday-calendar-2025">
              <Card hover className="text-center">
                <Calendar className="h-8 w-8 text-blue-500 mx-auto mb-2" />
                <h3 className="font-semibold">Holiday Calendar</h3>
              </Card>
            </Link>
            <Link to="/secret-santa-generator">
              <Card hover className="text-center">
                <Users className="h-8 w-8 text-green-500 mx-auto mb-2" />
                <h3 className="font-semibold">Secret Santa</h3>
              </Card>
            </Link>
            <Link to="/gift-guides">
              <Card hover className="text-center">
                <Gift className="h-8 w-8 text-purple-500 mx-auto mb-2" />
                <h3 className="font-semibold">Gift Guides</h3>
              </Card>
            </Link>
            <Link to="/price-tracker">
              <Card hover className="text-center">
                <TrendingUp className="h-8 w-8 text-orange-500 mx-auto mb-2" />
                <h3 className="font-semibold">Price Tracker</h3>
              </Card>
            </Link>
          </div>

          {/* Authentication Form for Guest Users */}
          {/* Create New Wishlist */}
          <Card className="mb-8">
            <h2 className="text-xl font-semibold mb-4">Create New Wishlist</h2>
            <form onSubmit={handleCreateWishlist} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Wishlist Title"
                  value={newWishlistTitle}
                  onChange={(e) => setNewWishlistTitle(e.target.value)}
                  placeholder="e.g., Christmas 2024, Birthday Gifts"
                  required
                />
                <Input
                  label="Description (Optional)"
                  value={newWishlistDescription}
                  onChange={(e) => setNewWishlistDescription(e.target.value)}
                  placeholder="Brief description of this wishlist"
                />
              </div>
              <Button type="submit" loading={isCreatingWishlist}>
                <Plus className="h-4 w-4 mr-2" />
                Create Wishlist
              </Button>
            </form>
          </Card>

          {/* My Wishlists */}
          <div>
            <h2 className="text-xl font-semibold mb-4">
              {user ? 'My Wishlists' : 'Your Wishlists'}
            </h2>
            
            {currentWishlistsLoading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner size="lg" />
              </div>
            ) : currentWishlists.length === 0 ? (
              <Card className="text-center py-12">
                <Heart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  No wishlists yet
                </h3>
                <p className="text-gray-600 mb-4">
                  Create your first wishlist to start planning gifts!
                </p>
                {!user && (
                  <p className="text-sm text-gray-500">
                    Your wishlists are saved locally. Sign in to sync across devices.
                  </p>
                )}
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {currentWishlists.map((wishlist) => (
                  <Link key={wishlist.id} to={`/wishlist/${wishlist.id}`}>
                    <Card hover>
                      <h3 className="font-semibold text-lg mb-2">{wishlist.title}</h3>
                      {wishlist.description && (
                        <p className="text-gray-600 text-sm mb-3">{wishlist.description}</p>
                      )}
                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <span>{wishlist.items?.length || 0} items</span>
                        <span>
                          {user 
                            ? (wishlist as any).is_public ? 'Public' : 'Private'
                            : 'Local'
                          }
                        </span>
                      </div>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Sign In Modal */}
      <SignInModal 
        isOpen={isSignInModalOpen} 
        onClose={() => setIsSignInModalOpen(false)}
        onOpenSignUp={() => setIsSignUpModalOpen(true)}
      />
      
      {/* Sign Up Modal */}
      <SignUpModal 
        isOpen={isSignUpModalOpen} 
        onClose={() => setIsSignUpModalOpen(false)} 
      />
    </>
  );
};
